<script>
import ResultDisplay from "@homepage/components/GenericResultDisplay.vue";
import Countdown from "@homepage/components/Countdown.vue";
import {generateImageMapFor} from "@homepage/helpers/image";
import PrizesList from "@homepage/components/PrizesList.vue";
import CardPresenter from "@homepage/components/CardPresenter.vue";
import LotteryResultAutoDisplay from "@homepage/components/LotteryResultAutoDisplay.vue";
import LotteryResult from "@homepage/components/LotteryResult.vue";

export default {
	name: 'QuickLottoBoard',
	props: {
		generalInformation: {
			type: Object,
			required: true
		}
	},
	components: {
		LotteryResult, LotteryResultAutoDisplay,
		CardPresenter,
		PrizesList,
		ResultDisplay,
		Countdown
	},
	provide() {
		return {
			imagesMap: generateImageMapFor('quickmoney'),
			generalInformation: this.generalInformation
		};
	},
	data() {
		return {
			timeToShowPrizesPage: 15000,
			timeToShowEachLotteryResultPage: 10000,
			lotteryEntriesPerPage: 2
		};
	},
	computed: {
		timeToShowResultsPage() {
			const timeToShowEachPage = this.timeToShowEachLotteryResultPage / 1000;

			return Math.ceil(this.generalInformation.lotteriesResultsEntries.length / this.lotteryEntriesPerPage) * timeToShowEachPage * 1000;
		}
	}
}
</script>

<template>
	<v-row justify="center" style="width: 1280px">
		<v-col cols="4">
			<v-row>
				<v-col class="d-flex justify-center align-center">
					<h1 class='font-weight-bold text-uppercase'>Winning numbers</h1>
				</v-col>
			</v-row>
			<v-row>
				<v-col class="main--text">
					<v-card
						v-for="result in generalInformation.results.slice(1,3)"
						:key="result.sortition.id"
						class="mb-2 rounded-bl-xl rounded-tr-xl result-card"
					>
						<result-display
							:result="result"
							:digits-image-dimensions="{height: '35px', width: '35px'}"
							:pick-three-image="{src: require('@/assets/img/quicklotto/pick3.png'), width: '100%', height: '53px',contain: true}"
							:pick-four-image="{src: require('@/assets/img/quicklotto/pick4.png'), width: '100%', height: '53px',contain: true}"
						>
						</result-display>
					</v-card>
				</v-col>
			</v-row>
		</v-col>
		<v-col cols="4">
			<v-row>
				<v-img width="100%" height="170px" contain
							 :src="require('@/assets/img/quicklotto/logo.png')">
				</v-img>
			</v-row>
			<v-row>
				<v-col class="d-flex white--text justify-center align-center">
					<h1 class='font-weight-bold text-uppercase'>Next draw in</h1>
				</v-col>
			</v-row>
			<v-row no-gutters>
				<countdown
					id="upcoming-result"
					class="text-center mt-5"
					:sortition="generalInformation.sortition">
					<template #default='{sortition}'>
						<v-flex md3>
							<h2 id='next-sortition' class='font-weight-bold'>{{ sortition.name }}</h2>
						</v-flex>
						<v-flex md3>
														<span id='time-left' v-if='sortition.timeLeft'
																	class='font-weight-black text-uppercase'>{{ sortition.timeLeft }}</span>
							<span v-else><font-awesome-icon icon='sync' spin size='3x'></font-awesome-icon></span>
						</v-flex>
					</template>
				</countdown>
			</v-row>
			<v-row no-gutters>
				<v-col cols="12">
					<div class="text-center text-h5"><span>Last Winning Numbers</span></div>
					<v-card
						v-for="result in generalInformation.results.slice(0, 1)"
						:key="result.sortition.id"
						class="rounded-bl-xl rounded-tr-xl result-card"
					>
						<result-display
							:digits-image-dimensions="{height: '40px', width: '40px'}"
							:pick-three-image="{src: require('@/assets/img/quicklotto/pick3.png'), width: '100%', height: '53px',contain: true}"
							:pick-four-image="{src: require('@/assets/img/quicklotto/pick4.png'), width: '100%', height: '53px',contain: true}"
							:result="result"
						>
						</result-display>
					</v-card>
				</v-col>
			</v-row>
		</v-col>
		<v-col cols="4">
			<v-row>
				<v-col>
					<v-row justify='center'>
						<v-col cols='auto'>
							<h1 class='white--text'>DRAWINGS</h1>
						</v-col>
					</v-row>
					<v-row justify='center'>
						<v-col v-for='sortition in generalInformation.sortedSortitions' cols='auto' :key='sortition.id'>
							<h1
								:class='{"red--text": generalInformation.sortition.id === sortition.id, "white--text": generalInformation.sortition.id !== sortition.id }'>
								{{ sortition.abbreviation }}
							</h1>
						</v-col>
					</v-row>
				</v-col>
			</v-row>
			<v-row>
				<v-col
					class="rounded-br-xl"
				>
					<card-presenter
						:time-per-page='[timeToShowPrizesPage, timeToShowResultsPage]'>
						<template #page-1>
							<v-row>
								<v-col class="d-flex align-center justify-center">
									<span class="headers">
										PAYOUTS
									</span>
								</v-col>
							</v-row>
							<prizes-list :general-information='generalInformation'>
								<template #prize-row='{prize}'>
									<v-col class='prize-row blue--bg white--text rounded-lg' cols='12'>
										<v-row no-gutters class='py-1'>
											<v-col class='prize-name text-center' cols='6'>
												{{ prize.name }} - 1X:
											</v-col>
											<v-col class='prize-amount text-center' cols='6'>
												{{ prize.amount }}
											</v-col>
										</v-row>
									</v-col>
								</template>
							</prizes-list>
							<div id="page-url" class="d-flex justify-center">
								<span>Visit us at <span class="text-decoration-underline">quicklotto.tv</span></span>
							</div>
						</template>
						<template #page-2>
							<v-card id='lottery-results' class='w-100 white--text align-center d-flex h-100 rounded-xl'>
								<lottery-result-auto-display
									:general-information='generalInformation'
									:entries-per-page='lotteryEntriesPerPage'
									:time-to-show-each-page='timeToShowEachLotteryResultPage'>
									<template #page='{ entries }'>
										<v-container class='px-0 py-0'>
											<lottery-result v-for='(entry, index) in entries'
																			class='lottery-result'
																			:class="{'rounded-b-xl': index === entries.length - 1}"
																			:key='entry.name'
																			:lottery='entry'>
												<template #sortition-header='{sortition, icon, date}'>
													<span class='sortition-header'>
														<font-awesome-icon :icon='icon'></font-awesome-icon>
														<span class="font-weight-bold">{{ sortition.name }}</span><br/> <span class="font-weight-bold">{{ date }}</span>
													</span>
												</template>
											</lottery-result>
										</v-container>
									</template>
								</lottery-result-auto-display>
							</v-card>
						</template>
					</card-presenter>
				</v-col>
			</v-row>
		</v-col>
	</v-row>
</template>

<style scoped lang="scss">

$main-color: #05a551;
$yellow: #F4D338;
$blue: #243468;
$font-color-3: #169b39;

* {
	font-family: "JackFrost", serif;
	color: $yellow;
}

.result-card > * {
	color: $blue;
}

#animated-clock {
	height: 80px;
	width: 100%;
}

.main--text {
	color: $main-color;
}

.blue--bg {
	background-color: $blue;
}

.headers {
	font-size: 1.5rem;
	font-weight: 800;
}

#next-sortition {
	font-size: 2.5rem;
	font-weight: 800;
	line-height: 35px;
	text-align: center;
}

#time-left {
	font-weight: bold;
	font-size: 2.6rem;
	text-align: center;
	color: white;
}

.prize-name {
	font-size: 1.8rem;
	font-weight: bold;
}

.prize-amount {
	font-size: 2rem;
	font-weight: bold;
}

#page-url {
	font-size: 1.3rem;
	font-weight: 800;
}

.sortition-header span {
	color: $blue;
}

::v-deep {
	.first-number, .second-number, .third-number {
		color: $blue;
	}

	.v-card {
		background-color: #ffffff80;
		border: 6px solid #159b39;
	}
}

#info-section {
	border-left: 5px solid $main-color;
	border-top: 5px solid $main-color;
	border-bottom: 5px solid $main-color;
}

::v-deep .sortition-name {
	font-size: 1.7rem;
	font-weight: bold;
}
</style>

<style>
#inspire {
	background-color: rgba(36, 52, 104, 0.9);
}
</style>
